import { EmailTemplateEditor } from '@/components/forms/EmailTemplateEditor';
import { Button } from '@/components/ui/Button';
import { domainsService } from '@/services/domains.service';
import { emailTemplatesService } from '@/services/emailTemplates.service';
import { useOrganisationsStore } from '@/store/organisations.store';
import {
  Cotisation,
  DsnData,
  EmailTemplate,
  EmailTemplateFormData,
  EstablishmentDsn,
} from '@/types/domain.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  Mail,
  Settings,
  X,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DsnDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  folderEmail?: string;
  onDataRefresh?: () => void;
}

interface DsnFormData {
  email: string;
  month: string;
  year: string;
}

const MONTHS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

/**
 * Période DSN par défaut = mois précédent (N-1), en base 1 (1-12), avec
 * gestion du passage d'année. En janvier → décembre de l'année précédente.
 * (Auparavant `new Date().getMonth()` donnait 0 en janvier — aucune option
 * correspondante dans le select — et ne gérait pas le changement d'année.)
 */
function getDefaultDsnPeriod(): { month: string; year: string } {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  if (currentMonth === 0) {
    return { month: '12', year: (now.getFullYear() - 1).toString() };
  }
  return { month: currentMonth.toString(), year: now.getFullYear().toString() };
}

export function DsnDrawer({
  isOpen,
  onClose,
  folderUuid,
  folderEmail,
  onDataRefresh,
}: DsnDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  const [formData, setFormData] = useState<DsnFormData>({
    email: folderEmail || '',
    ...getDefaultDsnPeriod(),
  });
  const [dsnResult, setDsnResult] = useState<DsnData | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [selectedTemplateForSending, setSelectedTemplateForSending] =
    useState<string>('');

  // Fetch DSN email templates from new service
  const { data: allTemplates } = useQuery({
    queryKey: ['email-templates-all', orgUuid],
    queryFn: () => emailTemplatesService.getAllTemplatesGrouped(),
    enabled: isOpen,
  });

  // Get DSN templates specifically
  const dsnTemplates = allTemplates?.dsn || [];

  const dsnMutation = useMutation({
    mutationFn: (params: { month: number; year: number; email: string }) =>
      domainsService.getDsnStructured(folderUuid, params),
    onSuccess: response => {
      setDsnResult(response.data);
      setShowResult(true);
      // Refresh parent data if callback provided
      onDataRefresh?.();
    },
  });

  // Template management mutations
  const createTemplateMutation = useMutation({
    mutationFn: (templateData: EmailTemplateFormData) =>
      emailTemplatesService.createEmailTemplate({
        ...templateData,
        templateType: 'dsn',
      }),
    onSuccess: response => {
      // Set the newly created template as selected for sending
      setSelectedTemplateForSending(response.data.uuid);
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      // Refresh templates list
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({
      templateUuid,
      templateData,
    }: {
      templateUuid: string;
      templateData: EmailTemplateFormData;
    }) => emailTemplatesService.updateEmailTemplate(templateUuid, templateData),
    onSuccess: () => {
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      // Refresh templates list
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
    },
  });

  const handleInputChange = (field: keyof DsnFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.email || !formData.month || !formData.year) {
      return;
    }

    dsnMutation.mutate({
      email: formData.email,
      month: parseInt(formData.month),
      year: parseInt(formData.year),
    });
  };

  const handleClose = () => {
    setFormData({
      email: folderEmail || '',
      ...getDefaultDsnPeriod(),
    });
    setDsnResult(null);
    setShowResult(false);
    setShowTemplateEditor(false);
    setSelectedTemplate(null);
    setSelectedTemplateForSending('');
    onClose();
  };

  const handleTemplateSave = async (templateData: EmailTemplateFormData) => {
    if (selectedTemplate) {
      await updateTemplateMutation.mutateAsync({
        templateUuid: selectedTemplate.uuid,
        templateData,
      });
    } else {
      await createTemplateMutation.mutateAsync(templateData);
    }
  };

  const handleOpenTemplateEditor = (template?: EmailTemplate) => {
    setSelectedTemplate(template || null);
    setShowTemplateEditor(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-xl max-w-xl bg-white shadow-xl transition-transform">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  {t('dsn.title', 'Envoyer la DSN par mail')}
                </h2>
                <p className="text-sm text-neutral-500">
                  {t('dsn.description', 'Générer et envoyer la DSN')}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {!showResult ? (
              <div className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-900">
                    {t('dsn.email', 'Adresse email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    placeholder={t('dsn.emailPlaceholder', 'email@example.com')}
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                  />
                </div>

                {/* Date Selection */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-900">
                      {t('dsn.period', 'Période')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-700">
                        {t('dsn.month', 'Mois')}
                      </label>
                      <select
                        value={formData.month}
                        onChange={e =>
                          handleInputChange('month', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-700">
                        {t('dsn.year', 'Année')}
                      </label>
                      <select
                        value={formData.year}
                        onChange={e =>
                          handleInputChange('year', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Email Template Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-neutral-400" />
                      <span className="text-sm font-medium text-neutral-900">
                        {t('dsn.emailTemplate', 'Template pour cet envoi')}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenTemplateEditor()}
                    >
                      {t('dsn.createTemplate', 'Créer un template')}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {/* Option par défaut */}
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
                      <input
                        type="radio"
                        name="template-selection"
                        value=""
                        checked={selectedTemplateForSending === ''}
                        onChange={() => setSelectedTemplateForSending('')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t(
                            'dsn.useDefaultTemplate',
                            'Utiliser le template par défaut'
                          )}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {t(
                            'dsn.defaultTemplateDescription',
                            'Template système configuré pour ce type'
                          )}
                        </p>
                      </div>
                    </label>

                    {/* Templates disponibles */}
                    {dsnTemplates.map(template => (
                      <label
                        key={template.uuid}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50"
                      >
                        <input
                          type="radio"
                          name="template-selection"
                          value={template.uuid}
                          checked={selectedTemplateForSending === template.uuid}
                          onChange={() =>
                            setSelectedTemplateForSending(template.uuid)
                          }
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-medium text-neutral-900">
                                {template.name}
                              </h5>
                              <p className="text-xs text-neutral-500">
                                {template.subject}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {template.isDefault && (
                                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700">
                                  {t('common.default', 'Défaut')}
                                </span>
                              )}
                              {!template.isActive && (
                                <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600">
                                  {t('common.inactive', 'Inactif')}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={e => {
                                  e.preventDefault();
                                  handleOpenTemplateEditor(
                                    template as EmailTemplate
                                  );
                                }}
                                className="text-neutral-400 hover:text-neutral-600"
                              >
                                <Settings className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}

                    {dsnTemplates.length === 0 && (
                      <div className="rounded-lg border border-dashed border-neutral-300 p-4 text-center">
                        <p className="text-sm text-neutral-500">
                          {t(
                            'dsn.noTemplatesAvailable',
                            'Aucun template personnalisé disponible'
                          )}
                        </p>
                        <p className="mt-1 text-xs text-neutral-400">
                          {t(
                            'dsn.createFirstTemplate',
                            'Créez votre premier template personnalisé ou utilisez le template par défaut'
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {formData.email && formData.month && formData.year && (
                  <div className="rounded-lg bg-neutral-50 p-4">
                    <h4 className="mb-2 text-sm font-medium text-neutral-900">
                      {t('dsn.summary', 'Résumé')}
                    </h4>
                    <div className="space-y-1 text-sm text-neutral-600">
                      <p>
                        <span className="font-medium">Email:</span>{' '}
                        {formData.email}
                      </p>
                      <p>
                        <span className="font-medium">Période:</span>{' '}
                        {MONTHS.find(m => m.value === formData.month)?.label}{' '}
                        {formData.year}
                      </p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {dsnMutation.isError && (
                  <div className="bg-error-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <XCircle className="text-error-600 h-4 w-4" />
                      <span className="text-error-900 text-sm font-medium">
                        {t('dsn.error', 'Erreur lors de la récupération')}
                      </span>
                    </div>
                    <p className="text-error-700 mt-1 text-sm">
                      {dsnMutation.error?.message ||
                        t('common.genericError', 'Une erreur est survenue')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // DSN Results
              <div className="space-y-6">
                {/* Header Info */}
                <div className="rounded-lg bg-blue-50 p-4">
                  <h3 className="font-medium text-blue-900">
                    {dsnResult?.folderName} ({dsnResult?.folderCode})
                  </h3>
                  <p className="text-sm text-blue-700">
                    {dsnResult?.monthName} {dsnResult?.year}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    {(dsnResult?.summary.establishmentsWithDSN ?? 0) > 0 ? (
                      <CheckCircle className="text-success-600 h-4 w-4" />
                    ) : (
                      <XCircle className="text-error-600 h-4 w-4" />
                    )}
                    <span className="text-sm font-medium">
                      {dsnResult?.message}
                    </span>
                  </div>
                </div>

                {/* Summary */}
                {dsnResult?.summary && (
                  <div className="rounded-lg border border-neutral-200 p-4">
                    <h4 className="mb-3 font-medium text-neutral-900">
                      Résumé
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-neutral-600">
                          Établissements:
                        </span>
                        <p className="font-medium">
                          {dsnResult.summary.establishmentsWithDSN} /{' '}
                          {dsnResult.summary.totalEstablishments}
                        </p>
                      </div>
                      <div>
                        <span className="text-neutral-600">
                          Effectif total:
                        </span>
                        <p className="font-medium">
                          {dsnResult.summary.totalEffectif}
                        </p>
                      </div>
                      <div>
                        <span className="text-neutral-600">
                          Total cotisations:
                        </span>
                        <p className="font-medium">
                          {formatCurrency(dsnResult.summary.totalCotisations)}
                        </p>
                      </div>
                      <div>
                        <span className="text-neutral-600">Coût global:</span>
                        <p className="font-medium">
                          {formatCurrency(dsnResult.summary.totalCoutGlobal)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Establishments */}
                <div className="space-y-4">
                  <h4 className="font-medium text-neutral-900">
                    Établissements
                  </h4>
                  {dsnResult?.establishments.map(
                    (establishment: EstablishmentDsn, index: number) => (
                      <div
                        key={index}
                        className="rounded-lg border border-neutral-200 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <h5 className="font-medium text-neutral-900">
                            {establishment.codeEtablissement}
                          </h5>
                          <div className="flex items-center gap-2">
                            {establishment.dsnExists ? (
                              <CheckCircle className="text-success-600 h-4 w-4" />
                            ) : (
                              <XCircle className="text-error-600 h-4 w-4" />
                            )}
                            <span className="text-sm">
                              {establishment.dsnExists
                                ? 'DSN disponible'
                                : 'Pas de DSN'}
                            </span>
                          </div>
                        </div>

                        {establishment.dsnExists &&
                          establishment.cotisations.length > 0 && (
                            <>
                              <div className="mb-3 grid grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="text-neutral-600">
                                    Cotisations:
                                  </span>
                                  <p className="font-medium">
                                    {formatCurrency(
                                      establishment.totalCotisations
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-neutral-600">
                                    Net versé:
                                  </span>
                                  <p className="font-medium">
                                    {formatCurrency(
                                      establishment.montantNetVerse
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-neutral-600">
                                    Coût global:
                                  </span>
                                  <p className="font-medium">
                                    {formatCurrency(establishment.coutGlobal)}
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <h6 className="text-sm font-medium text-neutral-700">
                                  Organismes
                                </h6>
                                {establishment.cotisations.map(
                                  (
                                    cotisation: Cotisation,
                                    cotIndex: number
                                  ) => (
                                    <div
                                      key={cotIndex}
                                      className="rounded bg-neutral-50 p-3 text-xs"
                                    >
                                      <div className="mb-1 flex items-start justify-between">
                                        <span className="font-medium">
                                          {cotisation.organismeInfo.nom}
                                        </span>
                                        <span className="font-medium">
                                          {formatCurrency(cotisation.montant)}
                                        </span>
                                      </div>
                                      <div className="text-neutral-600">
                                        <p>Mode: {cotisation.modePaiement}</p>
                                        <p>
                                          Échéance: {cotisation.dateEcheance}
                                        </p>
                                        {cotisation.datePaiement && (
                                          <p>
                                            Paiement: {cotisation.datePaiement}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </>
                          )}

                        {establishment.error && (
                          <div className="bg-error-50 text-error-700 mt-2 rounded p-2 text-sm">
                            {establishment.error}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 p-6">
            {!showResult ? (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !formData.email ||
                    !formData.month ||
                    !formData.year ||
                    dsnMutation.isPending
                  }
                  className="flex-1"
                >
                  {dsnMutation.isPending
                    ? t('common.loading', 'Chargement...')
                    : t('dsn.send', 'Envoyer')}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResult(false);
                    setDsnResult(null);
                  }}
                  className="flex-1"
                >
                  {t('common.back', 'Retour')}
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  {t('common.close', 'Fermer')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Template Editor */}
      <EmailTemplateEditor
        template={selectedTemplate}
        templateType="dsn"
        folderUuid="" // Not used anymore with new architecture
        isOpen={showTemplateEditor}
        onClose={() => {
          setShowTemplateEditor(false);
          setSelectedTemplate(null);
        }}
        onSave={handleTemplateSave}
        defaultTemplate={null}
      />
    </div>
  );
}
